/**
 * Stack de modales visibles para la tecla Escape en web.
 *
 * El modal de más arriba es dueño del Escape: si el tope no tiene handler
 * (modal bloqueado guardando, etc.) el Escape se consume igual y no baja a
 * modales inferiores — misma semántica que el back hardware de Android y que
 * el `activeModalStack` interno de react-native-web.
 *
 * Puro (sin react-native) para poder testearlo con node/vitest.
 */

export type ModalEscapeEntry = {
  id: number;
  /** null = modal sin cierre por Escape: tapa a los de abajo igual. */
  close: (() => void) | null;
};

const stack: ModalEscapeEntry[] = [];
let nextId = 1;
let lastDispatchAt = 0;

/** Apila un modal visible. Devuelve el id para desapilar. */
export function pushModalEscape(close: (() => void) | null): number {
  const id = nextId++;
  stack.push({ id, close });
  return id;
}

export function removeModalEscape(id: number): void {
  const index = stack.findIndex((entry) => entry.id === id);
  if (index >= 0) stack.splice(index, 1);
}

/**
 * Un Escape: dispara el handler del modal tope.
 * Devuelve true si había un modal registrado (consumió la tecla, haya o no
 * handler real) — false si no hay modales y el Escape es libre.
 */
export function dispatchModalEscape(): boolean {
  const top = stack[stack.length - 1];
  if (!top) return false;
  lastDispatchAt = Date.now();
  top.close?.();
  return true;
}

/**
 * Ms desde el último Escape despachado por el stack (Infinity si nunca).
 * Sirve para dedupar el `keyup` interno de RNW, que puede volver a llamar
 * `onRequestClose` en el mismo press físico.
 */
export function msSinceLastEscapeDispatch(): number {
  return lastDispatchAt === 0 ? Number.POSITIVE_INFINITY : Date.now() - lastDispatchAt;
}

export function modalEscapeStackSize(): number {
  return stack.length;
}

/** Solo para tests. */
export function __resetModalEscapeStack(): void {
  stack.length = 0;
  lastDispatchAt = 0;
}
